import type {
  MatchingCandidate,
  MatchingCandidateClient,
  MatchingCandidateSpeaker,
  MatchingResponse,
} from "@/api/types";

/**
 * Entity that should be highlighted inside artifact text. Built from the
 * Phase-6 matching bindings: speakers (employees) get `kind: "employee"`
 * and the meeting's client gets `kind: "client"`.
 */
export interface HighlightEntity {
  kind: "employee" | "client";
  entityId: string;
  /** Primary display name (Employee.full_name / Client.name). */
  name: string;
  /** Whether the candidate is in `pending` status — surfaced in the tooltip. */
  pending: boolean;
  /** Top-suggestion confidence; falls back to `null` when unbound. */
  confidence: number | null;
}

/** Output token from {@link splitTextByEntities}. */
export type HighlightToken =
  | { kind: "plain"; text: string }
  | { kind: "match"; text: string; entity: HighlightEntity };

/**
 * Build the list of highlight-eligible entities from a `MatchingResponse`.
 * Only bound candidates contribute — pending suggestions stay hidden until
 * the operator resolves them.
 */
export function entitiesFromMatching(
  matching: MatchingResponse | null,
): HighlightEntity[] {
  if (matching === null) return [];
  const out: HighlightEntity[] = [];
  if (matching.client !== null) {
    pushEntity(out, matching.client, "client");
  }
  for (const sp of matching.speakers) {
    pushEntity(out, sp, "employee");
  }
  return out;
}

function pushEntity(
  out: HighlightEntity[],
  cand: MatchingCandidateClient | MatchingCandidateSpeaker,
  kind: "employee" | "client",
): void {
  if (cand.bound_entity_id === null || cand.bound_display_name === null) {
    return;
  }
  out.push({
    kind,
    entityId: cand.bound_entity_id,
    name: cand.bound_display_name,
    pending: cand.status === "pending",
    confidence: topSuggestionConfidence(cand),
  });
}

function topSuggestionConfidence(cand: MatchingCandidate): number | null {
  if (cand.suggestions.length === 0) return null;
  const s = cand.suggestions[0];
  return s === undefined ? null : s.confidence;
}

/**
 * Walk `text` and split it into plain + matched tokens for every
 * highlight entity whose `name` appears verbatim in the text (case-
 * insensitive). Longer names take priority over shorter ones so that
 * "Иван Петров" wins over a bare "Иван" when both are in the registry.
 *
 * No regex escaping is needed because we anchor on `indexOf`. Overlapping
 * matches resolve left-to-right with the greedy longest-name preference.
 */
export function splitTextByEntities(
  text: string,
  entities: readonly HighlightEntity[],
): HighlightToken[] {
  if (entities.length === 0 || text.length === 0) {
    return [{ kind: "plain", text }];
  }
  // Sort longest-name first so multi-word matches win over substrings.
  const sorted = [...entities]
    .filter((e) => e.name.trim().length > 0)
    .sort((a, b) => b.name.length - a.name.length);
  if (sorted.length === 0) return [{ kind: "plain", text }];

  const lower = text.toLowerCase();
  // Hits = sorted, non-overlapping ranges with their entity.
  interface Hit {
    start: number;
    end: number;
    entity: HighlightEntity;
  }
  const taken: Hit[] = [];
  for (const entity of sorted) {
    const needle = entity.name.toLowerCase();
    let cursor = 0;
    while (cursor < lower.length) {
      const idx = lower.indexOf(needle, cursor);
      if (idx === -1) break;
      const end = idx + needle.length;
      if (!overlaps(taken, idx, end)) {
        taken.push({ start: idx, end, entity });
      }
      cursor = end;
    }
  }
  if (taken.length === 0) return [{ kind: "plain", text }];
  taken.sort((a, b) => a.start - b.start);
  const tokens: HighlightToken[] = [];
  let pos = 0;
  for (const hit of taken) {
    if (hit.start > pos) {
      tokens.push({ kind: "plain", text: text.slice(pos, hit.start) });
    }
    tokens.push({
      kind: "match",
      text: text.slice(hit.start, hit.end),
      entity: hit.entity,
    });
    pos = hit.end;
  }
  if (pos < text.length) {
    tokens.push({ kind: "plain", text: text.slice(pos) });
  }
  return tokens;
}

function overlaps(
  taken: readonly { start: number; end: number }[],
  start: number,
  end: number,
): boolean {
  for (const t of taken) {
    if (start < t.end && end > t.start) return true;
  }
  return false;
}
